import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, Subject, throwError, switchMap, tap } from 'rxjs';
import { ProblemDetails } from '../models/problem-details';
import { MessageService } from '../services/message.service';
import { AuthenticationService } from '../services/authentication.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private readonly messageService: MessageService,
        private readonly authenticationService: AuthenticationService,
        private readonly router: Router
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: any) => {
                if (error.error instanceof ErrorEvent) {
                    this.messageService.openUnexpectedError();
                    return throwError(() => error.error);
                }
                else {
                    switch (error.status) {
                        case 403:
                            break;
                        case 410:
                            // Do not display an error message, when the claim is expired
                            break;
                        case 409:
                            return this.authenticationService.logout()
                                .pipe(switchMap(() => {
                                    this.router.navigateByUrl(this.authenticationService.getLoginUrl(this.router.url), { state: { ignoreDirtyCheck: true } });
                                    this.authenticationService.pushSessionMissmatch();
                                    // Workaround to show the error message
                                    this.handleError(error).subscribe();
                                    // Error handling does not work for some reason here. switchMap after parseErrorBlob never fires.
                                    return this.handleError(error);
                                }));
                        case 401:
                            // Do not display an error message, since authentication interceptor will retry
                            break;
                        case 400:
                        case 404:
                        case 405:
                        case 500:
                            return this.handleError(error);
                        default:
                            this.messageService.openUnexpectedError();
                            break;
                    }
                }

                return throwError(() => error);
            })
        );
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        if (error.error?.detail || error.error?.title) {
            this.showProblemDetailsErrorMessage(error.error as ProblemDetails);
        } else if (error.error?.size) {
            return this.parseErrorBlob(error).pipe(
                switchMap(parsedError => {
                    if (typeof parsedError === 'string') {
                        this.messageService.openUnexpectedError();
                        return throwError(() => parsedError);
                    }

                    this.showProblemDetailsErrorMessage(parsedError);

                    return throwError(() => parsedError);
                })
            );
        }
        else if (error.error?.errors) {
            this.messageService.openErrorLabel(error.error.errors.join('\n'));
        }
        else {
            this.messageService.openUnexpectedError();
        }

        return throwError(() => error);
    }
    private showProblemDetailsErrorMessage(problemDetails?: ProblemDetails): void {
        if ((problemDetails as any)?.errors) {
            this.messageService.openError(Object.values((problemDetails as any).errors).join('\n'));
        }
        else if (problemDetails?.detail) {
            this.messageService.openError(problemDetails.detail);
        }
        else if (problemDetails?.title) {
            this.messageService.openError(problemDetails.title);
        }
        else {
            this.messageService.openUnexpectedError();
        }
    }

    private parseErrorBlob(errorResponse: HttpErrorResponse): Observable<ProblemDetails | string> {
        var subject = new Subject<ProblemDetails | string>();

        let reader = new FileReader();
        reader.onload = () => {
            let result = reader?.result as string;

            try {
                result = JSON.parse(result)
            }
            catch {
                // Response error is not a JSON object and might be a server error (HTML etc.)
            }

            subject.next(result);
        };
        reader.readAsText(errorResponse.error);

        return subject.asObservable();
    }
}